import { action, flow, observable, makeObservable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { DocumentStore } from '@roc/feature-documents';
import { LoanStore } from '@roc/feature-loans';
import { DealEconomicsStore } from './dealEconomicsStore';
import { ExpectedClosingDateStore } from './expectedClosingDateStore';
import { RequestedClosingDateStore } from './requestedClosingDateStore';
import { LoanPaymentsStore } from './loanPaymentsStore';
import { PointOfContactStore } from './pointOfContact/pointOfContactStore';
import { TamariskPaymentStore } from './tamariskPaymentStore';
import { BorrowerClosingAgentStore } from './borrowerClosingAgentStore';
import { InsuranceProviderStore } from './insuranceProvider/insuranceProviderStore';
import { InsuranceApprovalStore } from './insuranceApproval/insuranceApprovalStore';
import { FundingTemplateStore } from './fundingTemplateStore';
import BorrowerSurveyStore from './borrowerSurveyStore';
import BorrowerEntityStore from './borrowerEntity/borrowerEntityStore';
import LenderFeesStore from './lenderFeesStore';
import TitleAgentStore from './titleAgent/titleAgentStore';
import TitleAgentV2Store from './titleAgentV2/titleAgentV2Store';
import FundingPreferenceStore from './fundingPreference/fundingPreferenceStore';
import ExpectedWireReleaseDateStore from './expectedWireReleaseDateStore';
import { AppraisalOrderDetailsStore } from './appraisalOrderDetailsStore';
import { Document } from '@roc/feature-types';
import {
  DocumentName,
  DOCUMENT_FORMS,
  SCOPE_OF_WORK_FORM,
  SCOPE_OF_WORK_FORM_V2,
  SCOPE_OF_WORK_LINK_V2,
  RESOLUTION_ITEM_FORM,
  CLOSING_CONDITION,
  BANK_STATEMENT_PREFIX,
  LEASE_INFORMATION_UNIT_PREFIX,
  TAX_ESCROW,
  POST_DISASTER_INSPECTION,
  CREDIT_REPORT,
  TRIMERGE_CREDIT_REPORT,
  BANK_STATEMENT_MONTH_PREFIX,
  TRACK_RECORD_RESOLUTION_ITEM_FORM,
  CERTIFICATE_OF_GOOD_STANDING,
  MANUAL_TASK,
  TRACK_RECORD_RESOLUTION,
  PROPERTY_RESOLUTION,
  BORROWER_RESOLUTION,
  LEASE_AGREEMENT_PREFIX,
  BANK_STATEMENT_TASK_CODE,
  RESOLUTION_FORM,
  DOC_REVIEW_FORM,
  LIEN_RESOLUTION_DOCUMENT,
  ResolutionType,
  AUTOMATED_DOC_REVIEW_FORMS
} from '../../constants';
import { isNil } from '@roc/feature-utils';
import {
  isAccepted,
  isFileAlreadyUploaded,
  isFormDocument,
  documentUploadNotAllowedOnly,
  isPendingOrMoreInfoNeeded,
} from '../../utils/documents';
import TermSheetStore from './termSheetStore';
import { DocusignStore } from './docusignStore';
import { ScopeOfWorkV2Service } from '@roc/feature-sow-shared/services';
import {
  ScopeOfWorkV2FormBaseStore,
  ScopeOfWorkV2FormStore,
  ScopeOfWorkV2LinkStore,
} from '@roc/feature-sow-shared/stores/v2';
import { TitleCompanyBankInformationStore } from './titleCompanyBankInformation/titleCompanyBankInformationStore';
import LoanFastrackPaymentStore from './loanFastrackPaymentStore';
import LoanClosingConditionStore from '../loanClosingConditionStore';
import CdaAndAppraisalOrderDetailsStore from './cdaAndAppraisalOrderDetailsStore';
import { BorrowerSetupStore } from '@roc/feature-borrowers';
import { BankStatementFormStore } from './bankStatement/bankStatementFormStore';
import { LeaseInformationFormStore } from './leaseInformation/leaseInformationFormStore';
import { PostDisasterInspectionFormStore } from './postDisasterInspection/postDisasterInspectionFormStore';
import { PersonalIdentificationFormStore } from './personalIdentificationFormStore';
import { CmtFormStore } from './cmtFormStore';
import AppraisalRecertificationOrderDetailsStore from './appraisalRecertificationOrderDetailsStore';
import { PermitFormStore } from './permitsFormStore';
import { CreateClosingConditionStore } from './closingCondition/createClosingConditionStore';
import { CreatePostClosingConditionStore } from './postClosingCondition/createPostClosingConditionStore';
import { TakePropertyPhotosStore } from './takePropertyPhotosStore';
import ServicerProvidedDataStore from 'libs/feature-payoffs/stores/servicerProvidedDataStore';
import PayoffRequestDetailStore from 'libs/feature-payoffs/stores/payoffRequestDetailStore';
import ServicerFeeStore from 'libs/feature-payoffs/stores/servicerFeeStore';
import GenerateCemaStore from 'libs/feature-payoffs/stores/generateCemaStore';
import { InterestAllocationStore, PaReconciliationStore, PayoffCalculatorStore, PayoffReconciliationReviewStore } from '@roc/feature-payoffs';
import { FirstAppraisalValueConfirmationStore } from './firstAppraisalValueConfirmation/firstAppraisalValueConfirmationStore';
import { DocumentSigningStore } from './documentSigningStore';
import { UnderwriteTheLoanStore } from './underwriteTheLoan/underwriteTheLoanStore';
import { ExtensionDetailsStore } from './extensionDetailsStore';
import { ValuationDisputeStore } from './valuationDisputeStore';
import { AppraisalReassignmentLetterStore } from './appraisalReassignmentLetterStore';

export class DocumentFormStore {
  private documentStore: DocumentStore;
  public loanStore: LoanStore;
  private globalStore: GlobalStore;
  dealEconomicsStore: DealEconomicsStore;
  expectedClosingDateStore: ExpectedClosingDateStore;
  requestedClosingDateStore: RequestedClosingDateStore;
  loanPaymentsStore: LoanPaymentsStore;
  cdaAndAppraisalOrderDetailsStore: CdaAndAppraisalOrderDetailsStore;
  appraisalRecertificationOrderDetailsStore: AppraisalRecertificationOrderDetailsStore;
  termSheetStore: TermSheetStore;
  pointOfContactStore: PointOfContactStore;
  loanFastrackPaymentStore: LoanFastrackPaymentStore;
  tamariskPaymentStore: TamariskPaymentStore;
  borrowerClosingAgentStore: BorrowerClosingAgentStore;
  insuranceProviderStore: InsuranceProviderStore;
  borrowerSurveyStore: BorrowerSurveyStore;
  borrowerEntityStore: BorrowerEntityStore;
  lenderFeesStore: LenderFeesStore;
  titleAgentStore: TitleAgentStore;
  titleAgentV2Store: TitleAgentV2Store;
  fundingPreferenceStore: FundingPreferenceStore;
  insuranceApprovalStore: InsuranceApprovalStore;
  fundingTemplateStore: FundingTemplateStore;
  appraisalOrderDetailsStore: AppraisalOrderDetailsStore;
  expectedWireReleaseDateStore: ExpectedWireReleaseDateStore;
  scopeOfWorkV2FormStore: ScopeOfWorkV2FormStore;
  scopeOfWorkV2LinkStore: ScopeOfWorkV2LinkStore;
  scopeOfWorkV2Service: ScopeOfWorkV2Service;
  docusignStore: DocusignStore;
  titleCompanyBankInformationStore: TitleCompanyBankInformationStore;
  createClosingConditionStore: CreateClosingConditionStore;
  createPostClosingConditionStore: CreatePostClosingConditionStore;
  loanClosingConditionStore: LoanClosingConditionStore;
  borrowerSetupStore: BorrowerSetupStore;
  bankStatementFormStore: BankStatementFormStore;
  leaseInformationFormStore: LeaseInformationFormStore;
  postDisasterInspectionFormStore: PostDisasterInspectionFormStore;
  personalIdentificationFormStore: PersonalIdentificationFormStore;
  cmtFormStore: CmtFormStore;
  permitFormStore: PermitFormStore;
  takePropertyPhotosStore: TakePropertyPhotosStore;
  servicerProvidedDataStore: ServicerProvidedDataStore;
  payoffRequestDetailStore: PayoffRequestDetailStore;
  servicerFeeStore: ServicerFeeStore;
  generateCemaStore: GenerateCemaStore;
  interestAllocationStore: InterestAllocationStore;
  payoffCalculatorStore: PayoffCalculatorStore;
  payoffReconciliationReviewStore: PayoffReconciliationReviewStore;
  paReconciliationStore: PaReconciliationStore;
  firstAppraisalValueConfirmationStore: FirstAppraisalValueConfirmationStore;
  documentSigningStore: DocumentSigningStore;
  underwriteTheLoanStore: UnderwriteTheLoanStore;
  extensionDetailsStore: ExtensionDetailsStore;
  valuationDisputeStore: ValuationDisputeStore;
  appraisalReassignmentLetterStore: AppraisalReassignmentLetterStore;

  currentForm: string;

  constructor(
    documentStore: DocumentStore,
    loanStore: LoanStore,
    globalStore: GlobalStore
  ) {
    this.documentStore = documentStore;
    this.loanStore = loanStore;
    this.globalStore = globalStore;
    this.dealEconomicsStore = new DealEconomicsStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.expectedClosingDateStore = new ExpectedClosingDateStore(
      documentStore,
      loanStore,
      globalStore
    );
    this.requestedClosingDateStore = new RequestedClosingDateStore(
      documentStore,
      loanStore,
      globalStore
    );
    this.dealEconomicsStore = new DealEconomicsStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.termSheetStore = new TermSheetStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.loanPaymentsStore = new LoanPaymentsStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.cdaAndAppraisalOrderDetailsStore = new CdaAndAppraisalOrderDetailsStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.appraisalRecertificationOrderDetailsStore = new AppraisalRecertificationOrderDetailsStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.pointOfContactStore = new PointOfContactStore(globalStore, loanStore);
    this.loanFastrackPaymentStore = new LoanFastrackPaymentStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.tamariskPaymentStore = new TamariskPaymentStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.borrowerSurveyStore = new BorrowerSurveyStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.borrowerEntityStore = new BorrowerEntityStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.lenderFeesStore = new LenderFeesStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.loanClosingConditionStore = new LoanClosingConditionStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.titleAgentStore = new TitleAgentStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.titleAgentV2Store = new TitleAgentV2Store(
      loanStore,
      documentStore,
      globalStore
    );
    this.insuranceProviderStore = new InsuranceProviderStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.borrowerClosingAgentStore = new BorrowerClosingAgentStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.insuranceApprovalStore = new InsuranceApprovalStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.fundingTemplateStore = new FundingTemplateStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.expectedWireReleaseDateStore = new ExpectedWireReleaseDateStore(
      documentStore,
      loanStore,
      globalStore
    );
    this.appraisalOrderDetailsStore = new AppraisalOrderDetailsStore(
      globalStore
    );
    this.fundingPreferenceStore = new FundingPreferenceStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.docusignStore = new DocusignStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.titleCompanyBankInformationStore = new TitleCompanyBankInformationStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.createClosingConditionStore = new CreateClosingConditionStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.createPostClosingConditionStore = new CreatePostClosingConditionStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.takePropertyPhotosStore = new TakePropertyPhotosStore(
      loanStore,
      documentStore,
      globalStore
    );
    this.scopeOfWorkV2LinkStore = new ScopeOfWorkV2LinkStore(globalStore);
    this.scopeOfWorkV2FormStore = new ScopeOfWorkV2FormStore(globalStore);
    this.scopeOfWorkV2Service = new ScopeOfWorkV2Service();
    this.borrowerSetupStore = new BorrowerSetupStore(globalStore);
    this.bankStatementFormStore = new BankStatementFormStore(globalStore);
    this.leaseInformationFormStore = new LeaseInformationFormStore(globalStore);
    this.postDisasterInspectionFormStore = new PostDisasterInspectionFormStore(
      globalStore
    );
    this.personalIdentificationFormStore = new PersonalIdentificationFormStore(
      globalStore
    );
    this.cmtFormStore = new CmtFormStore(globalStore);
    this.permitFormStore = new PermitFormStore(globalStore);
    this.servicerProvidedDataStore = new ServicerProvidedDataStore(
      loanStore,
      documentStore,
      globalStore,
    );
    this.payoffRequestDetailStore = new PayoffRequestDetailStore(
      loanStore,
      documentStore,
      globalStore,
    );
    this.servicerFeeStore = new ServicerFeeStore(
      loanStore,
      documentStore,
      globalStore,
    );
    this.generateCemaStore = new GenerateCemaStore(
      loanStore,
      documentStore,
      globalStore,
    );
    this.interestAllocationStore = new InterestAllocationStore(globalStore);
    this.payoffCalculatorStore = new PayoffCalculatorStore(loanStore,
      documentStore,
      globalStore,
    );
    this.payoffReconciliationReviewStore = new PayoffReconciliationReviewStore(loanStore,
      documentStore,
      globalStore,
    );
    this.paReconciliationStore = new PaReconciliationStore(globalStore);
    this.firstAppraisalValueConfirmationStore = new FirstAppraisalValueConfirmationStore(loanStore, documentStore, globalStore);
    this.documentSigningStore = new DocumentSigningStore(globalStore);
    this.underwriteTheLoanStore = new UnderwriteTheLoanStore(globalStore);
    this.extensionDetailsStore = new ExtensionDetailsStore(globalStore, documentStore, loanStore);
    this.valuationDisputeStore = new ValuationDisputeStore(globalStore);
    this.appraisalReassignmentLetterStore = new AppraisalReassignmentLetterStore(globalStore);

    makeObservable(this, {
      setCurrentForm: action,
      closeCurrentForm: action,
      reset: action,
      currentForm: observable,
      openScopeOfWork: flow,
    });
    this.initialize();
  }

  initialize() {
    this.scopeOfWorkV2LinkStore.setOnSuccessLink(this.onScopeOfWorkLinked);
    this.scopeOfWorkV2FormStore.setOnSubmitScopeOfWork(
      this.onScopeOfWorkFormSubmitted
    );
    this.scopeOfWorkV2FormStore.setOnResetSuccefull(this.onResetSOW);
  }

  setCurrentForm(form: string) {
    this.currentForm = form;
  }

  closeCurrentForm() {
    this.setCurrentForm(null);
  }

  isFormDocument(document: Document) {
    const isForm = isFormDocument(
      document,
      this.loanStore?.userStore?.isNportalUser,
      this.loanStore?.loanDetails?.toDosFrameworkEnabled
    );
    const isFormNotAllowedOnly = this.globalStore?.userFeatures
      ?.drawsEntitlements?.isFormNotAllowedOnly || [];

    if (isFormNotAllowedOnly.includes(document.taskName)) {
      return false;
    } else if (document.taskName === DocumentName.PERSONAL_IDENTIFICATION) {
      return isForm && this.globalStore.isInternalPortal;
    } else {
      return isForm;
    }
  }

  isAppraisalReportDocument(documentName: string) {
    return Object.values([
      DocumentName.APPRAISAL_REPORT,
      DocumentName.APPRAISAL_REPORT_ARV,
      DocumentName.APPRAISAL_REPORT_AS_IS,
      DocumentName.APPRAISAL_REPORT_AS_IS_RENT,
    ]).includes(documentName as DocumentName);
  }

  canOpenForm(document: Document, loanProcess: string) {
    const isForm = this.isFormDocument(document);
    if (!isForm) return false;

    const isUploadNotAllowed = this.documentUploadNotAllowedOnly(document, loanProcess);

    const documentMeetsOpenFormRules = this.doesDocumentMeetsOpenFormRules(
      document
    );

    return !isUploadNotAllowed && documentMeetsOpenFormRules;
  }

  private doesDocumentMeetsOpenFormRules(document: Document) {
    if (!this.globalStore.isInternalPortal && document.taskName === DocumentName.LOAN_FUNDING_TEMPLATE) {
      return isAccepted(document);
    } else if (document.taskName === DocumentName.NOTE_ALLONGE) {
      return !isAccepted(document);
    } else if (document.taskName === DocumentName.ASSIGNMENT_AND_CONVEYANCE) {
      return !isAccepted(document);
    } else if (document.taskName === DocumentName.DRAW_REPORT) {
      return isAccepted(document);
    } else if (document.taskName === DocumentName.ACH_FORM) {
      return !isAccepted(document);
    } else if (!this.globalStore.isInternalPortal && document.taskName === DocumentName.DRAW_REQUEST_FORM) {
      return isPendingOrMoreInfoNeeded(document);
    } else {
      return true;
    }
  }

  openDocumentForm(document: Document) {
    if (document?.resolutionType == ResolutionType.LIEN) {
      this.setCurrentForm(LIEN_RESOLUTION_DOCUMENT);
    } else if (!!AUTOMATED_DOC_REVIEW_FORMS[document?.taskName]) {
      this.setCurrentForm(DOC_REVIEW_FORM);
    } else if (!!document?.resolutionType) {
      this.setCurrentForm(RESOLUTION_FORM);
    } else if (
      document.taskName === DocumentName.SCOPE_OF_WORK ||
      document.taskName === DocumentName.DIGITAL_SCOPE_OF_WORK
    ) {
      this.openScopeOfWork(document);
    }
    else if (document.taskName &&
      document.taskName.includes(TRACK_RECORD_RESOLUTION) &&
      document.taskInformation?.includes('track record')) {
      this.setCurrentForm(TRACK_RECORD_RESOLUTION_ITEM_FORM);
    }
    else if (document.taskName && document.taskName.includes(CERTIFICATE_OF_GOOD_STANDING)) {
      this.setCurrentForm(CERTIFICATE_OF_GOOD_STANDING);
    }
    else if (document.manualTask) {
      this.setCurrentForm(MANUAL_TASK);
    }
    else if (
      document.taskName &&
      document.taskName.includes(BORROWER_RESOLUTION) &&
      !document.taskName.includes('Insurance')
    ) {
      this.setCurrentForm(RESOLUTION_ITEM_FORM);

    } else if (document.taskName && document.taskName.includes(CLOSING_CONDITION)) {
      this.setCurrentForm(document.taskName)
    } else if (document?.taskName?.includes(PROPERTY_RESOLUTION)) {
      this.setCurrentForm(RESOLUTION_ITEM_FORM);
    } else if (document.taskName &&
      (document.taskName === TAX_ESCROW || document.taskName === POST_DISASTER_INSPECTION
        || document.taskName === CREDIT_REPORT || document.taskName === TRIMERGE_CREDIT_REPORT)) {
      this.setCurrentForm(document.taskName)
    } else if (document.taskName?.startsWith(LEASE_INFORMATION_UNIT_PREFIX)) {
      this.setCurrentForm(document.taskName);
    } else if (document.taskName?.startsWith(DocumentName.CLIENT_VALUATION_DISPUTE)) {
      this.setCurrentForm(DOCUMENT_FORMS[DocumentName.CLIENT_VALUATION_DISPUTE]);
    } else if (document.taskName?.startsWith(DocumentName.INTERNAL_VALUATION_ROV_FOR_APPRAISAL)) {
      this.setCurrentForm(DOCUMENT_FORMS[DocumentName.INTERNAL_VALUATION_ROV_FOR_APPRAISAL]);
    } else {
      this.setCurrentForm(DOCUMENT_FORMS[document.taskName]);
    }
  }

  openTermSheetForm(document: Document) {
    const { loanPaymentsUrl } = this.loanStore.loanDetails;

    if (loanPaymentsUrl) {
      this.setCurrentForm(DOCUMENT_FORMS[document.taskName][0]);
    } else {
      this.setCurrentForm(DOCUMENT_FORMS[document.taskName][1]);
    }
  }

  *openScopeOfWork(document: Document) {
    try {
      const { data: response } = yield this.scopeOfWorkV2Service.getScopeOfWork(
        document.loanId,
        document.collateralId
      );
      const scopeOfWork = JSON.parse(response.data);
      if (!scopeOfWork) {
        this.setCurrentForm(SCOPE_OF_WORK_LINK_V2);
      } else {
        this.openScopeOfWorkForm(scopeOfWork);
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message:
          'An error occurred while fetching the Scope of Work information',
      });
    }
  }

  onScopeOfWorkLinked = scopeOfWork => {
    this.openScopeOfWorkForm(scopeOfWork);
  };

  onResetSOW = () => {
    this.setCurrentForm(SCOPE_OF_WORK_LINK_V2);
  };

  openScopeOfWorkForm(scopeOfWork) {
    if (!this.globalStore.isInternalPortal) {
      const params = {
        scopeOfWorkId: scopeOfWork.scopeOfWorkId,
        isInternal: true,
        version: scopeOfWork.version,
      };
      this.scopeOfWorkV2FormStore.setParams(params);
    }
    this.setCurrentForm(SCOPE_OF_WORK_FORM_V2);
  }

  onScopeOfWorkFormSubmitted = (submitted: boolean) => {
    if (isNil(submitted)) return;
    this.closeCurrentForm();
  };

  onCloseNoteAllongeAndAssignment = () => {
    this.closeCurrentForm();
  };

  reset() {
    this.currentForm = null;
    this.dealEconomicsStore.reset();
    this.termSheetStore.reset();
    this.loanPaymentsStore.reset();
    this.cdaAndAppraisalOrderDetailsStore.reset();
    this.tamariskPaymentStore.reset();
    this.insuranceProviderStore.reset();
    this.borrowerSurveyStore.reset();
    this.borrowerEntityStore.reset();
    this.lenderFeesStore.reset();
    this.titleAgentStore.reset();
    this.titleAgentV2Store.reset();
    this.insuranceProviderStore.reset();
    this.fundingPreferenceStore.reset();
    this.insuranceApprovalStore.reset();
    this.fundingTemplateStore.reset();
    this.scopeOfWorkV2FormStore.reset();
    this.scopeOfWorkV2LinkStore.reset();
    this.loanClosingConditionStore.reset();
    this.borrowerSetupStore.reset();
    this.bankStatementFormStore.reset();
    this.leaseInformationFormStore.reset();
    this.postDisasterInspectionFormStore.reset();
    this.servicerProvidedDataStore.reset();
    this.payoffRequestDetailStore.reset();
    this.servicerFeeStore.reset();
    this.generateCemaStore.reset();
    this.valuationDisputeStore.reset();
    this.appraisalReassignmentLetterStore.resetStore();
  }

  private documentUploadNotAllowedOnly(
    document: Document,
    loanProcess: string
  ) {
    const entitlements = this.loanStore.getLoanProcessEntitlements(loanProcess, document);
    return documentUploadNotAllowedOnly(
      entitlements?.isUploadNotAllowedOnly,
      document
    );
  }
}

export default DocumentFormStore;
